import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getEmployeeData } from "./localStorage";
import { WINDOWS_TO_IANA } from "../constant/windowsToIana";

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_IANA = "Asia/Karachi";

/** Raw timeZone value from JWT token (Windows ID or IANA) */
export const getUserTimeZoneSetting = () => getEmployeeData()?.timeZone || null;

/** IANA timezone name for dayjs (from token timeZone setting) */
export const getUserIanaTimeZone = () => {
  const tz = getUserTimeZoneSetting();
  if (!tz) return DEFAULT_IANA;
  if (tz.includes("/")) return tz;
  return WINDOWS_TO_IANA[tz] || DEFAULT_IANA;
};

/** Current date/time in the user's configured timezone */
export const nowInUserTz = () => dayjs().tz(getUserIanaTimeZone());

/** Parse any date value into the user's configured timezone */
export const parseToUserTz = (date) => {
  if (date == null || date === "") return null;

  const tz = getUserIanaTimeZone();

  if (dayjs.isDayjs(date)) {
    return date.tz(tz);
  }

  if (typeof date === "string") {
    const trimmed = date.trim();
    if (!trimmed) return null;

    // Calendar date (YYYY-MM-DD) — treat as date in user timezone
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return dayjs.tz(trimmed, tz);
    }

    // ISO / datetime from API — convert UTC instant to user timezone
    if (trimmed.includes("T") || trimmed.endsWith("Z")) {
      const utcParsed = dayjs.utc(trimmed);
      return utcParsed.isValid() ? utcParsed.tz(tz) : null;
    }

    const tzParsed = dayjs.tz(trimmed, tz);
    if (tzParsed.isValid()) return tzParsed;

    const parsed = dayjs(trimmed);
    return parsed.isValid() ? parsed.tz(tz) : null;
  }

  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.tz(tz) : null;
};

/** Format a date in the user's timezone (default: DD/MM/YYYY) */
export const formatDate = (date, format = "DD/MM/YYYY") => {
  const parsed = parseToUserTz(date);
  if (!parsed?.isValid()) return "-";
  return parsed.format(format);
};

/** Format date + time in the user's timezone */
export const formatDateTime = (date, format = "DD/MM/YYYY HH:mm") => {
  const parsed = parseToUserTz(date);
  if (!parsed?.isValid()) return "-";
  return parsed.format(format);
};

/** Format time only (HH:mm) in the user's timezone */
export const formatTime = (date, format = "HH:mm") => {
  const parsed = parseToUserTz(date);
  if (!parsed?.isValid()) return "-";
  return parsed.format(format);
};

/** Normalize any date value to YYYY-MM-DD in user timezone */
export const toDateKey = (date) => {
  const parsed = parseToUserTz(date);
  return parsed?.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

/** Start/end of day in user timezone, returned as UTC ISO for API calls */
export const toUtcDayBounds = (date) => {
  const tz = getUserIanaTimeZone();
  const d = parseToUserTz(date) || nowInUserTz();
  return {
    start: d.tz(tz).startOf("day").utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
    end: d.tz(tz).endOf("day").utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
  };
};

export { dayjs };
